import { z } from 'zod';
import {
  OVERLAY_OPACITY_MAX,
  OVERLAY_OPACITY_MIN,
  OVERLAY_POSITIONS,
  OVERLAY_SIZE_PCT_MAX,
  OVERLAY_SIZE_PCT_MIN,
} from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';

// Overlays (docs/09 §C2): a layer rendered above all content — a brand logo pinned
// to a corner and/or a persistent ticker strip. Assigned per-screen (screens.overlay_id)
// or org-wide (orgs.default_overlay_id); the screen assignment wins.

export const overlayPositionSchema = z.enum(OVERLAY_POSITIONS);

export const overlaySizePctSchema = z
  .number()
  .int()
  .min(OVERLAY_SIZE_PCT_MIN)
  .max(OVERLAY_SIZE_PCT_MAX);

export const overlayOpacitySchema = z
  .number()
  .int()
  .min(OVERLAY_OPACITY_MIN)
  .max(OVERLAY_OPACITY_MAX);

export const overlaySchema = z.object({
  id: idSchema,
  org_id: idSchema,
  name: z.string().min(1),
  logo_media_id: idSchema.nullable(), // null = ticker-only overlay
  position: overlayPositionSchema,
  size_pct: overlaySizePctSchema,
  opacity: overlayOpacitySchema,
  ticker_text: z.string().nullable(), // null = no ticker strip
  created_at: unixMsSchema,
});
export type Overlay = z.infer<typeof overlaySchema>;

// POST /v1/overlays. At least one of logo_media_id / ticker_text must be present —
// enforced in the service (kept off the schema so the JSON Schema stays a clean object
// for the assistant tool surface).
export const createOverlaySchema = z.object({
  name: z.string().min(1),
  logo_media_id: idSchema.nullish(),
  position: overlayPositionSchema.default('br'),
  size_pct: overlaySizePctSchema.default(10),
  opacity: overlayOpacitySchema.default(90),
  ticker_text: z.string().min(1).nullish(),
});
export type CreateOverlay = z.infer<typeof createOverlaySchema>;

// PATCH /v1/overlays/:id — partial; the same "at least one of logo/ticker survives"
// invariant is re-checked against the merged row in the service.
export const updateOverlaySchema = z
  .object({
    name: z.string().min(1).optional(),
    logo_media_id: idSchema.nullish(),
    position: overlayPositionSchema.optional(),
    size_pct: overlaySizePctSchema.optional(),
    opacity: overlayOpacitySchema.optional(),
    ticker_text: z.string().min(1).nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'provide at least one field' });
export type UpdateOverlay = z.infer<typeof updateOverlaySchema>;
